import { Link } from "react-router-dom";
import ManagerCard from "../components/ManagerCard";
import CategoryIcon from "../components/CatIconMapper";
import { highlightText } from "../utils/TextHighLighter";
import InstitutionCard from "../components/InstitutionCard";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/Tabs";

type Props = {
  mainTab: "services" | "managements" | "institutions";
  setMainTab: (v: "services" | "managements" | "institutions") => void;
  searchQuery: string;
  hasSearchResults: boolean;
  activeLoading: boolean;
  activeError: any;
  displayCategories: any[];
  displayPersons: any[];
  displayProjects: any[];
  isMobile: boolean;
  activePerson: number | null;
  activeProject: number | null;
  onPersonClick: (i: number) => void;
  onProjectClick: (i: number) => void;
  showManagersMore?: boolean;
  showInstitutionsMore?: boolean;
  managersMoreLink?: string;
  showPersonsLoadMore?: boolean;
  onPersonsLoadMore?: () => void;
  isPersonsLoadingMore?: boolean;
  institutionsMoreLink?: string;
  showInstitutionsLoadMore?: boolean;
  onInstitutionsLoadMore?: () => void;
  isInstitutionsLoadingMore?: boolean;
};
export default function HomeTabs({
  mainTab,
  setMainTab,
  searchQuery,
  hasSearchResults,
  activeLoading,
  activeError,
  displayCategories,
  displayPersons,
  displayProjects,
  showPersonsLoadMore,
  onPersonsLoadMore,
  isPersonsLoadingMore,
  isMobile,
  activePerson,
  activeProject,
  onPersonClick,
  onProjectClick,
  showInstitutionsLoadMore = false,
  onInstitutionsLoadMore,
  isInstitutionsLoadingMore = false,
}: Props) {
  return (
    <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as any)}>
      {/* Tabs Header */}
      <TabsList className="flex w-full xl:w-[45%] 2xl:w-[34%] gap-x-2 xl:space-x-5">
        <TabsTrigger
          value="institutions"
          className="!w-1/3 text-xs data-[state=active]:text-white xl:text-lg xl:font-medium shadow xl:!rounded-[20px]"
        >
          {highlightText("موسسات", searchQuery)}
        </TabsTrigger>
        <TabsTrigger
          value="managements"
          className="!w-1/3 text-xs data-[state=active]:text-white xl:text-lg xl:font-medium shadow xl:!rounded-[20px]"
        >
          {highlightText("مدیران", searchQuery)}
        </TabsTrigger>
        <TabsTrigger
          value="services"
          className="!w-1/3 text-xs data-[state=active]:text-white xl:text-lg xl:font-medium shadow xl:!rounded-[20px]"
        >
          {highlightText("خدمات", searchQuery)}
        </TabsTrigger>
      </TabsList>

      {/* services */}
      <TabsContent
        value="services"
        className="xl:w-[60%] 2xl:w-[47%] mt-6 xl:mt-12 focus-visible:outline-none"
      >
        {activeLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeError ? (
          <div className="flex items-center justify-center h-64 text-red-500">
            خطا در بارگذاری خدمات: {activeError.message}
          </div>
        ) : hasSearchResults && displayCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-green-04 mb-2">نتیجه‌ای یافت نشد</div>
            <p className="text-sm text-green-04">
              هیچ خدمتی با عبارت "{searchQuery}" یافت نشد
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-y-4 xl:gap-10 place-items-center [direction:rtl]">
            {displayCategories.map((category, index) => (
              <Link
                key={category.id ?? category.name ?? index}
                to={`/services/${category.id}`}
                className="flex flex-col items-center gap-y-1 group cursor-pointer"
              >
                <div className="size-[70px] xl:size-[100px] flex items-center justify-center bg-white group-hover:bg-green-01 hover:border hover:border-[#C3D4D2] shadow transition-all duration-300 rounded-[25px] xl:rounded-[39px]">
                  <CategoryIcon
                    id={category.id}
                    className="size-6 xl:size-9 text-green-05"
                  />
                </div>

                <h2 className="font-estedad text-[10px] xl:h-8 truncate text-center line-clamp-2 group-hover:opacity-80 xl:text-base xl:text-nowrap font-normal text-green-05 transition-colors">
                  {highlightText(category.name, searchQuery)}
                </h2>
              </Link>
            ))}
          </div>
        )}
      </TabsContent>

      {/* MANAGERS */}
      <TabsContent
        dir="rtl"
        value="managements"
        className="xl:w-[90%] 2xl:w-[80%] mt-6 xl:mt-12 focus-visible:outline-none"
      >
        {activeLoading ? (
          <div className="flex justify-center h-64">
            <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeError ? (
          <div className="text-red-500 text-center">خطا در بارگذاری مدیران</div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
          >
            {displayPersons.map((person, index) => (
              <ManagerCard
                key={person.id ?? index}
                person={person}
                index={index}
                isActive={activePerson === index}
                searchQuery={searchQuery}
                onClick={onPersonClick}
              />
            ))}
          </div>
        )}
        {showPersonsLoadMore && (
          <div className="w-full flex items-center justify-center mt-14">
            <button
              onClick={onPersonsLoadMore}
              disabled={isPersonsLoadingMore}
              className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px]"
            >
              {isPersonsLoadingMore ? (
                <div className="size-3 border-2 border-green-04 border-t-transparent rounded-full animate-spin" />
              ) : (
                "بیشتر"
              )}
            </button>
          </div>
        )}
      </TabsContent>
      {/* INSTITUTIONS */}
      <TabsContent
        dir="rtl"
        value="institutions"
        className="xl:w-[90%] 2xl:w-[80%] mt-6 xl:mt-12 focus-visible:outline-none"
      >
        {activeLoading ? (
          <div className="flex justify-center h-64">
            <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeError ? (
          <div className="text-red-500 text-center">خطا در بارگذاری موسسات</div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
          >
            {displayProjects.map((project, index) => (
              <InstitutionCard
                key={project.id ?? index}
                project={project}
                index={index}
                isActive={activeProject === index}
                searchQuery={searchQuery}
                onClick={onProjectClick}
              />
            ))}
          </div>
        )}
        {showInstitutionsLoadMore && (
          <div className="w-full flex items-center justify-center mt-14">
            <button
              onClick={onInstitutionsLoadMore}
              disabled={isInstitutionsLoadingMore}
              className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px]"
            >
              {isInstitutionsLoadingMore ? (
                <div className="size-3 border-2 border-green-04 border-t-transparent rounded-full animate-spin" />
              ) : (
                "بیشتر"
              )}
            </button>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
